<script setup lang="ts">
  import VueDatePicker from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';

  const runtimeConfig = useRuntimeConfig();
  const { userLoggedIn, hasPermission, requireEmailVerification } = useWadAuth();
  const {currentTimestamp, currentTimestampFormatted, setCurrentTimestampOffset} = useCurrentTime();

  const pickerDate = ref(Date.now());

  const setPickerCurrentDate = () => {
    pickerDate.value = currentTimestamp.value;
  }

  const setDate = (date: Date) => {
    const offset = date.getTime() - Date.now();
    // If the offset is less than 5 seconds use the current date.
    setCurrentTimestampOffset(Math.abs(offset) < 5000 ? 0 : offset );
  }

  const eventsStore = useEventsStore();
  const {eventData} = storeToRefs(eventsStore);
  const route = useRoute();
  // Hamburger that opens the event menu drawer (components/page/main-menu.vue) and, on the right, the
  // avatar that opens the account menu (components/page/account-menu.vue). Every logged-in user has both,
  // including while the email still needs verifying, because "Sign out" lives in the account menu.
  const {open: mainMenuOpen, accountOpen, available: mainMenuAvailable} = useMainMenu();
  const {data: authData} = useAuth();
  const {fixedWidthImageParams} = useImageCdn();
  const headerUser = computed(() => authData.value?.data ?? null);
  // Title and back arrow of the current page (registered by CommonPageNavigation).
  const pageHeader = usePageHeader();

  // Header QR icon — only inside an event. Users who can scan leads jump straight to the lead
  // scanner; everyone else opens their own event badge (QR) modal.
  const NuxtLink = resolveComponent('NuxtLink')
  const eventId = computed(() => eventData?.value?.id || null)
  const canScanLeads = computed(() => hasPermission('app.scan-leads'))
  const leadScanRoute = computed(() => (
    eventId.value ? {name: 'events-eventId-leads-scan', params: {eventId: eventId.value}} : null
  ))
  const badgeModalOpen = ref(false)
  const onQrClick = () => {
    if(!canScanLeads.value) {
      badgeModalOpen.value = true
    }
  }

  // region Notifications
  const isOnline = useOnline();
  const notificationsStore = useNotificationsStore();
  const {hasUnreadNotifications} = storeToRefs(notificationsStore);
  watch(userLoggedIn, async (loggedIn) => {
    if(loggedIn && (!import.meta.client || isOnline.value)) {
      await notificationsStore.loadNotifications();
    }
  }, {immediate: true})
  //endregion
</script>

<template>
  <div class="wad4-app-header__container">
    <client-only>
      <div class="wad4-app-header" :class="{ 'wad4-app-header--guest': !userLoggedIn }">
        <!-- Top-level pages have the menu. A detail page has the back arrow in its place: it leads up to the list the
             page was opened from in one tap (useUpNavigation), where the menu is. (The menu used to stay next to the
             arrow because the arrow went back one page at a time, so the menu could be many taps away.) -->
        <button v-if="mainMenuAvailable && !pageHeader?.showBack"
                type="button"
                class="wad4-app-header__icon-button wad4-app-header__menu"
                aria-label="Open menu"
                aria-haspopup="dialog"
                :aria-expanded="mainMenuOpen"
                @click="mainMenuOpen = true">
          <svg class="wad4-svg-icon--24" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <button v-if="userLoggedIn && pageHeader?.showBack"
                type="button"
                class="wad4-app-header__icon-button wad4-app-header__back"
                :aria-label="pageHeader.backLabel ? `Back to ${pageHeader.backLabel}` : 'Back'"
                @click="pageHeader.goBack()">
          <svg class="wad4-svg-icon--24" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span v-if="pageHeader.backLabel" class="wad4-app-header__back-label">{{ pageHeader.backLabel }}</span>
        </button>
        <!-- Logged-in users have the logo in the main menu; only a guest, who has no menu, needs it here. -->
        <NuxtLink v-if="!userLoggedIn" :to="{name: 'user-login'}" class="wad4-app-header__logo">
          <svg width="56" height="17" viewBox="0 0 56 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M30.3623 0.000103207H26.2731L21.7545 11.699L17.236 0.000103207H13.1468L8.60776 11.699L4.0892 0.000103207H0L6.56316 17H10.6524L15.1914 5.30115L19.7099 17H23.7991L28.3177 5.30115L32.8363 17H36.9459L30.3623 0.000103207Z" fill="var(--wad4-c-text-text-brand-wad)"/>
            <path d="M38.9889 0V4.04179L50.7045 8.48979L38.9889 12.9581V16.9999L56 10.5208V6.47905L38.9889 0Z" fill="var(--wad4-c-text-text-brand-wad)"/>
          </svg>
        </NuxtLink>
        <div class="wad4-app-header__center">
          <!-- No title next to a back arrow: the arrow says where it leads instead, and the page's own heading says
               what the page is. -->
          <span v-if="userLoggedIn && pageHeader?.title && !pageHeader.showBack" class="wad4-app-header__title">{{ pageHeader.title }}</span>
          <vue-date-picker v-else-if="userLoggedIn && !pageHeader?.title && runtimeConfig.public.allowTimeOverride === 'true'"
              :action-row="{ showNow: true }"
              :model-value="pickerDate"
              @open="setPickerCurrentDate"
              @update:model-value="setDate"
          >
            <template #trigger>
              {{currentTimestampFormatted}}
            </template>
          </vue-date-picker>
        </div>
        <nuxt-link v-if="userLoggedIn" :to="{name: 'notifications'}" class="wad4-app-header__icon-button wad4-app-header__notifications" aria-label="Notifications">
          <svg class="wad4-svg-icon--24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="-2.94 -2.25 24 24" fill="none">
            <path fill="currentColor" d="M14.3117 6.75C14.3117 5.35773 13.759 4.02263 12.7746 3.03809C11.79 2.05352 10.4541 1.5 9.06171 1.5C7.66949 1.50013 6.33425 2.05363 5.34979 3.03809C4.36538 4.02263 3.81171 5.35773 3.81171 6.75V7.49902L3.80194 7.9502C3.71727 9.82773 3.08783 11.6342 2.00116 13.1562C3.38035 13.5954 4.81483 13.9114 6.29315 14.0869C8.13248 14.3051 9.99191 14.3051 11.8312 14.0869L12.506 13.9971C13.7321 13.8151 14.9405 13.5321 16.1213 13.1562C14.9483 11.5126 14.3087 9.5367 14.3117 7.49902V6.75ZM11.3088 15.6494C9.81375 15.7843 8.30966 15.7843 6.81464 15.6494C6.80801 15.8016 6.81653 15.955 6.841 16.1064C6.89753 16.4559 7.03568 16.7872 7.24432 17.0732C7.45316 17.3594 7.72732 17.5924 8.04315 17.7529C8.35878 17.9133 8.70769 17.997 9.06171 17.9971C9.4159 17.9971 9.76546 17.9134 10.0812 17.7529C10.3971 17.5924 10.6712 17.3594 10.8801 17.0732C11.0887 16.7872 11.2269 16.4559 11.2834 16.1064C11.3079 15.955 11.3154 15.8017 11.3088 15.6494ZM15.8117 7.50098C15.8087 9.53942 16.5639 11.5064 17.9299 13.0195C18.0966 13.2042 18.1612 13.4596 18.1017 13.7012C18.0422 13.9428 17.8664 14.1394 17.633 14.2256C16.0684 14.8031 14.4495 15.218 12.8019 15.4678C12.8238 15.7598 12.8109 16.0547 12.7639 16.3457C12.6696 16.9285 12.439 17.4811 12.091 17.958C11.743 18.4348 11.2872 18.8224 10.7609 19.0898C10.2345 19.3573 9.65216 19.4971 9.06171 19.4971C8.47142 19.497 7.8897 19.3573 7.36346 19.0898C6.83723 18.8224 6.38141 18.4348 6.03339 17.958C5.6854 17.4811 5.45476 16.9285 5.36054 16.3457C5.31356 16.055 5.29977 15.7604 5.32147 15.4688C3.65068 15.2153 2.03445 14.7958 0.490418 14.2256C0.256933 14.1394 0.0811374 13.9429 0.021668 13.7012C-0.0377551 13.4596 0.0268227 13.2042 0.193543 13.0195C1.55982 11.5066 2.31447 9.53954 2.31171 7.50098V6.75C2.31171 4.95979 3.02338 3.24243 4.28925 1.97656C5.55498 0.710961 7.27179 0.000126262 9.06171 0C10.8518 0 12.5693 0.710841 13.8351 1.97656C15.101 3.24243 15.8117 4.95979 15.8117 6.75V7.50098Z"/>
          </svg>
          <svg v-if="hasUnreadNotifications" class="wad4-app-header__notifications-new" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <circle cx="5" cy="5" r="4"/>
          </svg>
        </nuxt-link>
        <component
            :is="canScanLeads ? NuxtLink : 'button'"
            v-if="userLoggedIn && eventId && !requireEmailVerification"
            :to="canScanLeads ? leadScanRoute : undefined"
            :type="canScanLeads ? undefined : 'button'"
            class="wad4-app-header__icon-button"
            :aria-label="canScanLeads ? 'Lead scanning' : 'My event badge'"
            @click="onQrClick"
        >
          <svg class="wad4-svg-icon--24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="-3 -3 24 24" fill="none">
            <path fill="currentColor" d="M6.75 11.625C6.75 11.4182 6.58179 11.25 6.375 11.25H1.875C1.66821 11.25 1.5 11.4182 1.5 11.625V16.125C1.5 16.2245 1.53905 16.3203 1.60938 16.3906C1.6797 16.4609 1.77554 16.5 1.875 16.5H6.375C6.58179 16.5 6.75 16.3318 6.75 16.125V11.625ZM6.75 1.875C6.75 1.66821 6.58179 1.5 6.375 1.5H1.875C1.66821 1.5 1.5 1.66821 1.5 1.875V6.375C1.5 6.47446 1.53905 6.5703 1.60938 6.64062C1.6797 6.71095 1.77554 6.75 1.875 6.75H6.375C6.58179 6.75 6.75 6.58179 6.75 6.375V1.875ZM16.5 1.875C16.5 1.66821 16.3318 1.5 16.125 1.5H11.625C11.4182 1.5 11.25 1.66821 11.25 1.875V6.375C11.25 6.47446 11.289 6.5703 11.3594 6.64062C11.4297 6.71095 11.5255 6.75 11.625 6.75H16.125C16.3318 6.75 16.5 6.58179 16.5 6.375V1.875ZM8.25 16.125C8.25 17.1602 7.41021 18 6.375 18H1.875C1.37772 18 0.90046 17.8028 0.548828 17.4512C0.197198 17.0995 0 16.6223 0 16.125V11.625C0 10.5898 0.839786 9.75 1.875 9.75H6.375C7.41021 9.75 8.25 10.5898 8.25 11.625V16.125ZM8.25 6.375C8.25 7.41021 7.41021 8.25 6.375 8.25H1.875C1.37772 8.25 0.900459 8.0528 0.548828 7.70117C0.197198 7.34954 0 6.87228 0 6.375V1.875C0 0.839786 0.839786 0 1.875 0H6.375C7.41021 0 8.25 0.839786 8.25 1.875V6.375ZM18 6.375C18 7.41021 17.1602 8.25 16.125 8.25H11.625C11.1277 8.25 10.6505 8.0528 10.2988 7.70117C9.9472 7.34954 9.75 6.87228 9.75 6.375V1.875C9.75 0.839786 10.5898 0 11.625 0H16.125C17.1602 0 18 0.839786 18 1.875V6.375Z"/>
            <path fill="currentColor" d="M11.25 15.5C11.6642 15.5 12 15.8358 12 16.25V17C12 17.4142 11.6642 17.75 11.25 17.75H10.5C10.0858 17.75 9.75 17.4142 9.75 17V16.25C9.75 15.8358 10.0858 15.5 10.5 15.5H11.25ZM17.25 15.5C17.6642 15.5 18 15.8358 18 16.25V17C18 17.4142 17.6642 17.75 17.25 17.75H16.5C16.0858 17.75 15.75 17.4142 15.75 17V16.25C15.75 15.8358 16.0858 15.5 16.5 15.5H17.25ZM4.5 12.5C4.91421 12.5 5.25 12.8358 5.25 13.25V14C5.25 14.4142 4.91421 14.75 4.5 14.75H3.75C3.33579 14.75 3 14.4142 3 14V13.25C3 12.8358 3.33579 12.5 3.75 12.5H4.5ZM14.25 12.5C14.6642 12.5 15 12.8358 15 13.25V14C15 14.4142 14.6642 14.75 14.25 14.75H13.5C13.0858 14.75 12.75 14.4142 12.75 14V13.25C12.75 12.8358 13.0858 12.5 13.5 12.5H14.25ZM11.25 9.5C11.6642 9.5 12 9.83579 12 10.25V11C12 11.4142 11.6642 11.75 11.25 11.75H10.5C10.0858 11.75 9.75 11.4142 9.75 11V10.25C9.75 9.83579 10.0858 9.5 10.5 9.5H11.25ZM17.25 9.5C17.6642 9.5 18 9.83579 18 10.25V11C18 11.4142 17.6642 11.75 17.25 11.75H16.5C16.0858 11.75 15.75 11.4142 15.75 11V10.25C15.75 9.83579 16.0858 9.5 16.5 9.5H17.25ZM4.5 2.75C4.91421 2.75 5.25 3.08579 5.25 3.5V4.25C5.25 4.66421 4.91421 5 4.5 5H3.75C3.33579 5 3 4.66421 3 4.25V3.5C3 3.08579 3.33579 2.75 3.75 2.75H4.5ZM14.25 2.75C14.6642 2.75 15 3.08579 15 3.5V4.25C15 4.66421 14.6642 5 14.25 5H13.5C13.0858 5 12.75 4.66421 12.75 4.25V3.5C12.75 3.08579 13.0858 2.75 13.5 2.75H14.25Z"/>
          </svg>
        </component>
        <button v-if="mainMenuAvailable && headerUser"
                type="button"
                class="wad4-app-header__icon-button wad4-app-header__avatar-button"
                aria-label="Open account menu"
                aria-haspopup="dialog"
                :aria-expanded="accountOpen"
                @click="accountOpen = true">
          <img class="wad4-app-header__avatar" v-bind="fixedWidthImageParams(headerUser.image_url, 28, 1)" alt="">
        </button>
        <event-badge-modal v-if="userLoggedIn && eventId && !requireEmailVerification" v-model="badgeModalOpen" :event-id="eventId"/>
      </div>
    </client-only>
  </div>
</template>

<style lang="scss">
  // 20px, not 12: the icons' 44px tap areas would otherwise overlap their neighbours.
  $app-header-gap: 20px;

  // The page title and the back label ("‹ Agenda") are the same words in the same place: going from a list to a detail
  // page only swaps the menu icon for the arrow. The label used to be the body face at 14px, so the word changed font,
  // size, colour and position on every tap into a detail page.
  @mixin app-header-title-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: $wad4-default-font-display;
    font-weight: $wad4-font-weight-medium;
    @include text-size-mixin('lg');
    color: $wad4-c-text-text-primary;
  }

  .wad4-app-header {
    display: flex;
    gap: $app-header-gap;
    align-items: center;
    padding-left: $wad4-container-padding;
    padding-right: $wad4-container-padding;
    background-color: $wad4-c-background-bg-primary;
    color: $wad4-c-foreground-fg-primary;
    height: calc($wad4-header-height - 1px);
    justify-content: space-between;
    max-width: 1656px;
    margin-left: auto;
    margin-right: auto;

    &__container {
      position: sticky;
      // Above everything a page pins or slides under it (the agenda's filter bar and time headers, My Event's
      // header, the offline banner: 21 at most), below the overlays (reminder hint 50, menus and dialogs 1000+).
      // At 2 the agenda's filter bar (20) was painted over the menu and title for the 0.2 s it slid back in.
      z-index: 30;
      top: 0;
      background-color: $wad4-c-background-bg-primary;
      // Total height includes the notch inset so the bar keeps its full height below the notch
      // (with border-box a fixed height would let padding-top eat into the bar instead of clearing the notch).
      height: $wad4-header-height-total;
      border-bottom: 1px solid $wad4-c-border-border-subtle;
      // Push header below iOS notch/Dynamic Island in standalone PWA mode
      padding-top: env(safe-area-inset-top, 0px);
    }

    &__logo {
      display: inline-flex;
    }

    &__icon-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      // A 24px icon is a poor thumb target. 10px of padding makes the tap area 44px and the equal
      // negative margin keeps the icon where it was, so the layout does not change.
      padding: 10px;
      margin: -10px;
      background: none;
      border: none;
      font: inherit;
      cursor: pointer;
      color: $wad4-c-foreground-fg-primary;
      transition: color 0.15s ease;

      // Hover and active share the same brand state (matches the bottom-nav item states).
      &.router-link-active {
        color: $wad4-c-foreground-fg-brand-solid;
      }

      @include hover {
        color: $wad4-c-foreground-fg-brand-solid;
      }
    }

    // The back arrow and where it leads ("‹ Agenda"). The label truncates rather than pushing the icons off the bar.
    &__back {
      // The header's gap, so the label starts where the title does next to the menu icon.
      gap: $app-header-gap;
      min-width: 0;

      // The label gives way to a long title, the arrow never does.
      svg {
        flex-shrink: 0;
      }

      @include hover {
        .wad4-app-header__back-label {
          color: $wad4-c-foreground-fg-brand-solid;
        }
      }
    }

    &__back-label {
      @include app-header-title-type;
      transition: color 0.15s ease;
    }

    // The avatar is 28px, so 8px of padding (and the equal negative margin) gives the same 44px tap area.
    &__avatar-button {
      padding: 8px;
      margin: -8px;
    }

    &__avatar {
      display: block;
      width: 28px;
      height: 28px;
      border-radius: $wad4-border-radius-full;
      object-fit: cover;
    }

    &__notifications {
      position: relative;

      &-new {
        position: absolute;
        // Anchored to the bell glyph, not the 44px button: the 24px icon is centred in it.
        top: 8px;
        right: 11px;

        & circle {
          fill: $wad4-c-foreground-fg-error-secondary;
        }
      }
    }

    // The page title, in the display face. Truncates rather than pushing the icons off the bar.
    &__title {
      display: block;
      @include app-header-title-type;
    }

    &__center {
      flex: 1;
      min-width: 0;
      color: $wad4-c-text-text-primary;
      @include text-size-mixin('sm');
      // Left, next to the menu or back button, not centred between the icons on both sides.
      text-align: left;

      @include mqBreakPoint(480px) {
        @include text-size-mixin('lg');
      }
    }
    &--guest{
      justify-content: center;
      width: 100%;
    }
  }

</style>
